import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { API } from '@src/api';
import {
  BulkReferralResult,
  FindhelpAttributeTag,
  FindhelpCategory,
  FindhelpProgram,
} from '@src/api.types';
import { TMemberInfo, trimZipcodeToFiveDigits } from '@repo/utils';
import { CboSearchParams } from '@src/routes/SearchImproved/SearchImproved';
import { findRecursive, findRecursivePathv2 } from '@src/utills/utils';
import React, { useEffect, useRef, useState } from 'react';
import { CboCardFindhelp } from '../CboCard/CboCardFindhelp';
import {
  CategoryData,
  CboCategoriesPanel,
  coerceFindhelpCategories,
} from '../CboCategories/CboCategories';
import { CboDetailModal } from '../CboDetailsModal/CboDetailsModal';
import { CboMap, intoMarkerTarget } from '../CboMap/CboMap';
import { CboMapAzure } from '../CboMap/CboMapAzure';
import { CboReferralSubmissionResults } from '../CboReferralSubmissionResults/CboReferralSubmissionResults';
import {
  CboSearchCategoryBreadcrumbs,
  findhelpCatsToGeneric,
  getBreadcrumbTrail,
} from '../CboSearchCategoryBreadcrumbs/CboSearchCategoryBreadcrumbs';
import {
  CboSearchFiltersPanel,
  intoFilterSection,
} from '../CboSearchFiltersPanel/CboSearchFiltersPanel';
import { CboSearchNoResultsFound } from '../CboSearchNoResultsFound/CboSearchNoResultsFound';
import { CboSearchPaginationTopBar } from '../CboSearchPaginationTopBar/CboSearchPaginationTopBar';
import { EmailMember } from '../Email/EmailMember';
import { printFindhelp } from '@src/utills/printProgram';
import { LoadingSpinnerModal } from '../LoadingSpinnerModal/LoadingSpinnerModal';
import { ReferralFormModal } from '../ReferralForm/ReferralForm';
import { BulkReferDropdownButton } from '@repo/ui/src/BulkReferDropdownButton';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import {
  getBulkReferral,
  getFeatureFlagsFromStorage,
  removeFromBulkReferral,
  setBulkReferral,
  updateBulkReferral,
  clearBulkReferral,
} from '@src/utills/storage';
import { BulkReferModal } from '../BulkReferModal';
import { event } from '@uhg-abyss/web/tools/event';

import eventEmitter from 'eventEmitter';
import { CommonModal } from '@repo/ui/src/CboSubcategoryModal';
import { loggedInAdvocateAtom } from '@src/global-state';
import { useAtomValue } from 'jotai';

export type SearchResultsWrapperFindhelpProps = {
  azureMapsApiKey: string;
  searchParameters: CboSearchParams;
  setSearchParameters: (next: CboSearchParams) => void;
  member: TMemberInfo;
  vendor: 'findhelp' | 'Unite Us';
  userId: string;
  favoritesFolderNames: string[];
  favoritedProgramIds: Set<string>;
  findhelpCategoryData: FindhelpCategory[];
  onSaveFavorite: (folderName: string, programId: string) => void;
  onRemoveFavorite: (programId: string) => void;
  currentZipcode?: string;
};

export function CboSearchWrapperFindhelp({
  azureMapsApiKey,
  searchParameters,
  setSearchParameters,
  member,
  vendor,
  userId,
  favoritesFolderNames,
  favoritedProgramIds,
  findhelpCategoryData,
  onSaveFavorite,
  onRemoveFavorite,
  currentZipcode,
}: SearchResultsWrapperFindhelpProps) {
  const loggedInAdvocate = useAtomValue(loggedInAdvocateAtom);
  // Count here is the size of the complete result-set, which is server-side paginated.
  // So if we have 10 records per page, count could be 35 (or some large number) while the
  // count of "programs" array is still maximum of 10.
  const [programSearchResults, setProgramSearchResults] = useState<
    | undefined
    | {
        count: number;
        programs: FindhelpProgram[];
      }
  >(undefined);

  const [availableFilters, setAvailableFilters] = useState<
    FindhelpAttributeTag[] | undefined
  >(undefined);

  const [showCategoryModalFor, setShowCategoryModalFor] = useState<
    CategoryData | undefined
  >(undefined);

  const [bulkReferring, setBulkReferringState] = useState<FindhelpProgram[]>(
    // TODO - Consider moving this to the top-level search component
    // FIXME - Type should be more generic since it can include non-findhelp programs
    // Controls the state of the checkbox on each program card for bulk referrals
    () => {
      return getBulkReferral();
    }
  );

  // Controls referral modal visibility
  const [referringTo, setReferringTo] = useState<FindhelpProgram | undefined>(
    undefined
  );

  const [referring, setReferring] = useState<FindhelpProgram[] | undefined>(
    undefined
  );

  const [referralResponse, setReferralResponse] = useState<any>(undefined);

  const [bulkReferralResponse, setBulkReferralResponse] = useState<
    BulkReferralResult | null | undefined
  >(undefined);


  // Controls email modal visibility
  const [emailingProgram, setEmailingProgram] = useState<
    FindhelpProgram | undefined
  >(undefined);

  // Controls visibility of details view modal
  const [showDetailsFor, setShowDetailsFor] = useState<
    FindhelpProgram | undefined
  >(undefined);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [clickedCategory, setClickedCategory] = useState<string>('');
  const [showFilter, setShowFilter] = useState(false);

  // Track the previous vendor so we can detect when it switches
  const prevVendorRef = useRef<string | undefined>(vendor);

  // side-effect: every time a program is added or removed from the bulk referral list,
  // update session storage so that the list is persisted throughout the app
  useEffect(() => {
    setBulkReferral(bulkReferring);
  }, [bulkReferring]);

  useEffect(() => {
    if (bulkReferralResponse?.failures?.length === 0) {
      setBulkReferringState([]);
      setReferring(undefined);
    }
  }, [bulkReferralResponse]);

  const setBulkReferring = (newBulkReferring: FindhelpProgram[]) => {
    setBulkReferringState(newBulkReferring);
    setBulkReferral(newBulkReferring);
  };

  const cancelBulkReferring = () => {
    clearBulkReferral();
    window.location.reload();
  };

  useEffect(() => {
    // Only run findhelp searches when the active vendor is findhelp.
    if (vendor !== 'findhelp') {
      prevVendorRef.current = vendor;
      setProgramSearchResults(undefined);
      setShowFilter(false);
      return;
    }

    // If the vendor just switched to findhelp, the category in searchParameters may
    // still belong to the previous vendor. Clear results and wait for a fresh category.
    if (prevVendorRef.current !== 'findhelp') {
      prevVendorRef.current = vendor;
      setProgramSearchResults(undefined);
      setShowFilter(false);
      return;
    }
    prevVendorRef.current = vendor;

    // Clear results immediately when category is removed so stale data is not shown.
    if (!searchParameters.category || searchParameters.category === '') {
      setProgramSearchResults(undefined);
      setShowFilter(false);
      return;
    }

    async function fetchPrograms() {
      setShowFilter(true);
      if (!findhelpCategoryData) {
        console.warn(
          'No category data is available, so programs search could not be performed.'
        );
        return;
      }

      setIsLoading(true);

      // Scary-looking type is just a union of the two possible API calls' awaited promises
      let resp:
        | undefined
        | Awaited<
            ReturnType<
              | typeof API.getFindHelpProgramsUsingNeedandZip
              | typeof API.getFindHelpProgramsUsingKeywords
            >
          > = undefined;

      // Look for a category whose label matches the search parameter
      const catMatchingTerm = findRecursivePathv2(
        searchParameters.category,
        findhelpCategoryData
      );

      if (catMatchingTerm === undefined) {
        // If the user's search term doesn't match a category label,
        // then it is a keyword search.
        const req: Parameters<typeof API.getFindHelpProgramsUsingKeywords>[0] =
          {
            zipcode: searchParameters.zipcode,
            terms: searchParameters.category,
            // NOTE - Search using terms does not have attributeTags / filters parameter. Why?
            // attributeTags: filters,
            sortBy: searchParameters.sortBy,
            page: searchParameters.page,
            recordsPerPage: searchParameters.recordsPerPage,
          };

        resp = await API.getFindHelpProgramsUsingKeywords(req);
      } else {
        // If the user's search term matches a category label,
        // then it is a category search.
        const req: Parameters<
          typeof API.getFindHelpProgramsUsingNeedandZip
        >[0] = {
          zipcode: searchParameters.zipcode,
          serviceTags: [searchParameters.category],
          attributeTags: Array.from(
            new Set(
              searchParameters.filters.filter((fl) => {
                return fl !== '';
              })
            )
          ),
          sortBy: searchParameters.sortBy,
          page: searchParameters.page,
          recordsPerPage: searchParameters.recordsPerPage,
        };

        resp = await API.getFindHelpProgramsUsingNeedandZip(req);
      }

      if (!resp || ('status' in resp && resp.status === 'error')) {
        setProgramSearchResults(undefined);
        // TODO - Show feedback to user about the error
        setIsLoading(false);
        return;
      }

      if (
        !!resp &&
        'status' in resp &&
        resp.status === 'success' &&
        'data' in resp
      ) {
        // For some reason, "no results" comes back as an empty object
        // rather than the object looking like { "count": 0, "programs": []}.
        if (
          typeof resp.data === 'object' &&
          Object.keys(resp.data).length === 0
        ) {
          // No results
          setProgramSearchResults({ count: 0, programs: [] });
          setIsLoading(false);
          return;
        }

        if (resp.data && 'count' in resp.data && 'programs' in resp.data) {
          const order = [200, 150, 100, 0];
          const orderMap = new Map(order.map((score, index) => [score, index]));

          resp.data.programs = resp.data.programs.sort((a, b) => {
            const indexA = orderMap.get(a.wl_score ?? 0) ?? order.length;
            const indexB = orderMap.get(b.wl_score ?? 0) ?? order.length;
            return indexA - indexB;
          });

          setProgramSearchResults(resp.data);
          setIsLoading(false);
          return;
        }
      }
    }

    // Only perform the programs search if we have the two required parameters
    if (searchParameters.zipcode) {
      fetchPrograms();
    }
  }, [vendor, searchParameters, findhelpCategoryData]);

  const getAttributeTagsZip = async () => {
    setIsLoading(true);

    const allTagsResp = await API.getFindhelpAttributeTagsAll();
    const filters = allTagsResp?.data;
    const filtersArray: any[] = Array.isArray(filters) ? filters : [];

    const locationResp = await API.getLocationSpecificFindhelpAttributeTags(
      searchParameters.zipcode
    );
    const locationData: any[] = Array.isArray(locationResp?.data)
      ? locationResp.data
      : [];

    const availableFilterLabels: string[] = [];
    for (const d of locationData) {
      if (d?.label) availableFilterLabels.push(String(d.label).toLowerCase());
    }

    for (const f of filtersArray) {
      f.filtersAvailable = false;

      const childrenArray: any[] = Array.isArray(f.children) ? f.children : [];
      for (const f2 of childrenArray) {
        try {
          const titleLower = String(f2?.title ?? '').toLowerCase();
          if (availableFilterLabels.indexOf(titleLower) != -1) {
            f2.filtersAvailable = true;
            f.filtersAvailable = true;
          }
        } catch (e) {}
      }
    }

    setAvailableFilters(filters);
    setIsLoading(false);
  };
  useEffect(() => {
    if (vendor !== 'findhelp' || searchParameters.zipcode == '') return;
    getAttributeTagsZip();
  }, [vendor, searchParameters.zipcode]);
  // Print functionality removed in V2 migration - handled directly via printFindhelp().

  const featureFlagsFromStorage = getFeatureFlagsFromStorage();

  const [zipcode, setZipCode] = useState<string>(() => {
    return trimZipcodeToFiveDigits(searchParameters.zipcode);
  });

  useEffect(() => {
    const handleZipChange = (newZip: string) => {
      setZipCode(newZip);
    };

    eventEmitter.on('zipChange', handleZipChange);

    return () => {
      eventEmitter.off('zipChange', handleZipChange);
    };
  }, []);

  const updateSearchParams = (catname: any, catid: any) => {
    window.location.href = `/search?category=${catname}&categoryId=${catid}&zipcode=${searchParameters.zipcode}`;
  };

  return (
    <React.Fragment>
      {/* Modals */}
      {
        // Loading state
        isLoading && <LoadingSpinnerModal isLoading={isLoading} />
      }
      {isModalOpen !== undefined && findhelpCategoryData && (
        <CommonModal
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          currentTopLevelCategory={clickedCategory}
          categoryData={findhelpCategoryData}
          onClickCategoryFilter={(clicked: FindhelpCategory) => {
            setSearchParameters({
              ...searchParameters,
              category: clicked.label,
              categoryId: clicked.id,
              filters: [],
              page: 1,
              zipcode: currentZipcode ?? '',
            });
          }}
        />
      )}

      {
        // Show the email modal if the user clicks the email button on a program card
        emailingProgram !== undefined && (
          <EmailMember
            vendor="findhelp"
            programData={emailingProgram}
            isOpen={emailingProgram !== undefined}
            setIsOpen={(next: boolean) => {
              if (!next) setEmailingProgram(undefined);
            }}
          />
        )
      }
      {
        // User is referring a member, show the referral modal
        referringTo !== undefined && (
          <ReferralFormModal
            vendor="findhelp"
            referringTo={referringTo}
            onClose={() => {
              setReferringTo(undefined);
            }}
            setReferResponse={setReferralResponse}
            memberInContext={member}
            paCoalitionConsent={
              featureFlagsFromStorage?.agent_pennsylvania_coalition_consent
                .enabled
            }
            loggedInAdvocate={loggedInAdvocate}
          />
        )
      }
      {
        // User is referring a member, show the referral modal
        referring !== undefined && (
          <BulkReferModal
            vendor="findhelp"
            referring={referring}
            selectedPrograms={bulkReferring}
            onClose={() => {
              setReferring(undefined);
              setBulkReferring(getBulkReferral());
            }}
            setReferResponse={setBulkReferralResponse}
          />
        )
      }
      {
        // User clicked a program card, show the details modal
        showDetailsFor !== undefined && (
          <CboDetailModal
            vendor="findhelp"
            isOpen={showDetailsFor !== undefined}
            program={showDetailsFor}
            onClose={() => {
              return setShowDetailsFor(undefined);
            }}
            isPrinting={false}
          />
        )
      }
      {
        // Submitted a referral, show the user a pop-up on success or failure
        (referralResponse !== undefined ||
          bulkReferralResponse !== undefined) && (
          <CboReferralSubmissionResults
            referResp={referralResponse}
            setReferResp={setReferralResponse}
            fromBulkRefer={bulkReferralResponse}
            setBulkReferResp={setBulkReferralResponse}
          />
        )
      }

      {/* Top-level container. Padding on the left & right to align with top bar. */}
      <div
        style={{
          paddingLeft: '5rem',
          paddingRight: '5rem',
        }}
      >
        {/* Category / "service tags" button panel */}
        {findhelpCategoryData && (
          <div
            style={{
              paddingTop: 4,
            }}
          >
            <CboCategoriesPanel
              categoryData={coerceFindhelpCategories(findhelpCategoryData)}
              onClickCategoryButton={(clicked: CategoryData) => {
                setClickedCategory(clicked.label);
                setIsModalOpen(true);
              }}
              vendor={vendor!}
            />
          </div>
        )}

        {showFilter ? (
          <Grid
            style={{
              paddingTop: '16px',
            }}
          >
            {/* Left column: Search results info, pagination, and program cards */}
            <Grid.Col
              span={{
                xs: 12,
                lg: 8,
              }}
              style={{
                paddingBottom: '8px',
                paddingLeft: 0,
                paddingTop: 0,
              }}
            >
              {programSearchResults &&
                Array.isArray(programSearchResults?.programs) &&
                // If we have zero results, show the "no results found" component.
                (programSearchResults.count <= 0 ? (
                  <div style={{ paddingTop: '32px' }}>
                    <CboSearchNoResultsFound
                      searchTerm={searchParameters.category}
                    />
                  </div>
                ) : (
                  <React.Fragment>
                    <Grid.Col span={{ xs: 12 }}>
                      <CboSearchPaginationTopBar
                        totalRecords={programSearchResults.count}
                        recordsPerPage={searchParameters.recordsPerPage}
                        currentPage={searchParameters.page}
                        onPageChange={(next: number) => {
                          setSearchParameters({
                            ...searchParameters,
                            page: next,
                          });
                        }}
                        onRecordsPerPageChange={(next: number) => {
                          setSearchParameters({
                            ...searchParameters,
                            page: 1,
                            recordsPerPage:
                              next as typeof searchParameters.recordsPerPage,
                          });
                        }}
                      />
                    </Grid.Col>

                    <Grid.Col
                      span={{ xs: 12 }}
                      style={{
                        paddingTop: 0,
                        paddingBottom: 8,
                      }}
                    >
                      <Grid>
                        <Grid.Col
                          span={{ xs: 12, sm: 6 }}
                          style={{
                            // Aligns this text with the bulk refer button
                            marginTop: 12,
                          }}
                        >
                          {/* Category search breadcrumbs (showing the taxonomy of category / sub-category) */}
                          {findhelpCategoryData && (
                            <CboSearchCategoryBreadcrumbs
                              breadcrumbTrail={getBreadcrumbTrail(
                                searchParameters.categoryId,
                                findhelpCategoryData.map(findhelpCatsToGeneric)
                              )}
                              onClickCrumb={(catname: any, catid: any) => {
                                updateSearchParams(catname, catid);
                              }}
                            />
                          )}
                        </Grid.Col>

                        <Grid.Col
                          span={{ xs: 12, sm: 6 }}
                          style={{
                            textAlign: 'right',
                          }}
                        >
                          <Flex direction="row" justify="flex-end">
                            <BulkReferDropdownButton
                              onClickSave={() => setReferring(bulkReferring)}
                              onClickCancel={cancelBulkReferring}
                              bulkReferringLength={bulkReferring.length}
                            />
                          </Flex>
                        </Grid.Col>
                      </Grid>
                    </Grid.Col>

                    <Grid.Col
                      span={{ xs: 12 }}
                      css={{
                        'abyss-grid-col': {
                          padding: '0px',
                        },
                      }}
                    >
                      <div
                        style={{
                          overflowY: 'scroll',
                          overflowX: 'hidden',
                          scrollbarWidth: 'thin',
                          maxHeight: '70vh',
                        }}
                        id="search-results-container"
                      >
                        {programSearchResults?.programs.map((program, idx) => {
                          return (
                            <div
                              id={`${program.id}-card-wrapper`}
                              key={idx}
                              style={{
                                paddingTop: 8,
                                paddingBottom: 8,
                              }}
                            >
                              <CboCardFindhelp
                                program={program}
                                isBulkReferring={bulkReferring.some((p) => {
                                  return p.id === program.id;
                                })}
                                allCategories={findhelpCategoryData.map(findhelpCatsToGeneric)}
                                showBulkReferralCheckbox={true}
                                onClickBulkReferToggle={() => {
                                  if (
                                    bulkReferring.some((p) => {
                                      return p.id === program.id;
                                    })
                                  ) {
                                    setBulkReferring(
                                      bulkReferring.filter((p) => {
                                        return p.id !== program.id;
                                      })
                                    );
                                    removeFromBulkReferral(program.id);
                                  } else {
                                    setBulkReferring([
                                      ...bulkReferring,
                                      program,
                                    ]);
                                    updateBulkReferral(program);
                                  }
                                }}
                                onClickRefer={() => {
                                  return setReferringTo(program);
                                }}
                                onClickTitle={() => {
                                  return setShowDetailsFor(program);
                                }}
                                onClickService={(service: string,id:any) => {
                                  // Update the search parameters to find programs providing
                                  // the selected service.
                                  // Reset the page number to one, but keep the existing filters.
                                  setSearchParameters({
                                    ...searchParameters,
                                    category: service,
                                    categoryId: Number(id),
                                    page: 1,
                                  });
                                }}
                                onClickServingAttribute={(
                                  attribute: string
                                ) => {
                                  // Add the attribute tag to the current list of filters
                                  setSearchParameters({
                                    ...searchParameters,
                                    page: 1,
                                    filters: [
                                      ...searchParameters.filters,
                                      attribute,
                                    ],
                                  });
                                }}
                                onClickPrint={() => {
                                  printFindhelp(program);
                                }}
                                onClickEmail={() => {
                                  event.setData({
                                    userId: userId,
                                  });
                                  event('EMAIL_CLICK');
                                  setEmailingProgram(program);
                                }}
                                availableFavoritesFolders={favoritesFolderNames}
                                isFavorite={favoritedProgramIds.has(program.id)}
                                onSaveFavorite={(inFolder: string) => {
                                  onSaveFavorite(inFolder, program.id);
                                }}
                                onRemoveFavorite={() => {
                                  onRemoveFavorite(program.id);
                                }}
                                member={member}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </Grid.Col>
                  </React.Fragment>
                ))}
            </Grid.Col>

            {/* Side-panel: Map & filters */}
            <Grid.Col
              span={{
                xs: 12,
                lg: 4,
              }}
              style={{
                position: 'sticky',
                overflowY: 'scroll',
                scrollbarWidth: 'thin',
                maxHeight: '75vh',
              }}
            >
              {(() => {
                const markerTargets = programSearchResults?.programs
                  .map(intoMarkerTarget)
                  .filter((tgt): tgt is NonNullable<typeof tgt> => tgt !== undefined);

                const handleViewProgramLink = (programId: string) => {
                  const card = document.getElementById(
                    `${programId}-card-wrapper`
                  );
                  if (!card) {
                    console.warn(
                      `Could not find card with id ${programId}-card`
                    );
                    return;
                  }
                  card.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                  });
                };

                return (featureFlagsFromStorage as any)?.googlemaps_enabled?.enabled ? (
                  <CboMap
                    apiKey={azureMapsApiKey}
                    programSearchResults={markerTargets ?? []}
                    onClickViewProgramLink={handleViewProgramLink}
                  />
                ) : (
                  <CboMapAzure
                    subscriptionKey={azureMapsApiKey}
                    programSearchResults={markerTargets ?? []}
                    onClickViewProgramLink={handleViewProgramLink}
                  />
                );
              })()}

              <hr
                style={{
                  marginTop: '16px',
                  marginBottom: '16px',
                  border: '0px solid #E0E0E0',
                }}
              />

              {availableFilters === undefined ? (
                <LoadingSpinner
                  size="xl"
                  isLoading={true}
                  ariaLoadingLabel="Downloading filters"
                />
              ) : (
                <CboSearchFiltersPanel
                  availableFilters={availableFilters
                    .map(intoFilterSection)
                    .filter((f) => {
                      return f !== undefined;
                    })
                    .map((f) => {
                      return f!;
                    })
                    .sort((a, b) => {
                      return a.heading.localeCompare(b.heading);
                    })}
                  activeFilters={searchParameters.filters}
                  setActiveFilters={(active: string[]) => {
                    setSearchParameters({
                      ...searchParameters,
                      filters: active,
                    });
                  }}
                  sortBy={searchParameters.sortBy}
                  setSortBy={(next) => {
                    setSearchParameters({
                      ...searchParameters,
                      sortBy: next,
                    });
                  }}
                />
              )}
            </Grid.Col>
          </Grid>
        ) : (
          ''
        )}
      </div>
    </React.Fragment>
  );
}
